// ============================================================
// components/snap-stash/SnapStashSheet.tsx
//
// The Snap & Stash flow (FR-W6 steps → UI). Desktop: centered
// card; mobile: full-width page. Focus moves to each step's
// heading; a polite live region narrates turn progress
// (FR-W13). All styling rides the shared design tokens.
// ============================================================
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapStashStore } from '../../store/snap-stash/context';
import { useStashScopedStore } from '../../store/stash-scoped/context';
import { PhotoPickStep } from './PhotoPickStep';
import { PhotoThumbnail } from './PhotoThumbnail';
import { CandidateChooser } from './CandidateChooser';
import { FieldConfirmList } from './FieldConfirmList';
import { EnrichedReview } from './EnrichedReview';
import { CategoryPills } from './CategoryPills';
import { MatchResolutionSheet } from './MatchResolutionSheet';
import { DoneStep } from './DoneStep';
import { QuotaState } from './QuotaState';

const STEP_HEADINGS: Record<string, string> = {
  idle: 'Snap a business',
  uploading: 'Snap a business',
  choosing: 'Which business?',
  confirming: 'Confirm the details',
  enriching: 'Confirm the details',
  reviewing: 'Review & save',
  committing: 'Review & save',
  resolving: 'Review & save',
  done: 'Saved!',
  error: 'Something went wrong',
};

const ERROR_COPY: Record<string, string> = {
  payloadTooLarge: 'That photo is too large — try a smaller one.',
  unsupportedImage: 'We couldn’t read that image — try a different photo.',
  serverError: 'Something went wrong on our side.',
  network: 'We couldn’t reach STASH — check your connection.',
  auth: 'Please sign in again to keep snapping.',
};

export function SnapStashSheet(): React.JSX.Element {
  const navigate = useNavigate();
  const flow = useSnapStashStore((state) => state.flow);
  const slowTurn = useSnapStashStore((state) => state.slowTurn);
  const restartNotice = useSnapStashStore((state) => state.restartNotice);
  const editedFields = useSnapStashStore((state) => state.editedFields);
  const selectedCategories = useSnapStashStore((state) => state.selectedCategories);
  const photoPreviewUrl = useSnapStashStore((state) => state.photoPreviewUrl);
  const startWithFile = useSnapStashStore((state) => state.startWithFile);
  const chooseCandidate = useSnapStashStore((state) => state.chooseCandidate);
  const setField = useSnapStashStore((state) => state.setField);
  const confirmAndEnrich = useSnapStashStore((state) => state.confirmAndEnrich);
  const toggleCategory = useSnapStashStore((state) => state.toggleCategory);
  const save = useSnapStashStore((state) => state.save);
  const resolveMatch = useSnapStashStore((state) => state.resolveMatch);
  const retry = useSnapStashStore((state) => state.retry);
  const reset = useSnapStashStore((state) => state.reset);
  const allCategories = useStashScopedStore((state) => state.categories);

  const heading = STEP_HEADINGS[flow.step] ?? 'Snap & Stash';
  const headingRef = useRef<HTMLHeadingElement>(null);
  const pendingTurn = flow.step === 'uploading' || flow.step === 'enriching' || flow.step === 'committing';

  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-6">
      <div className="rounded-3xl border border-border bg-card p-5 shadow-sm">
        <h2 ref={headingRef} tabIndex={-1} className="m-0 text-lg font-semibold text-foreground outline-none">
          {heading}
        </h2>
        <p aria-live="polite" className="m-0 min-h-5 text-xs text-muted-foreground">
          {pendingTurn ? (slowTurn ? 'Still working — big photos can take a few seconds…' : 'Working…') : ''}
        </p>

        {(flow.step === 'idle' || flow.step === 'uploading') && (
          <PhotoPickStep onPick={(file) => void startWithFile(file)} restartNotice={restartNotice} />
        )}

        {flow.step === 'choosing' && (
          <CandidateChooser
            candidates={flow.candidates}
            observed={flow.observed}
            onChoose={chooseCandidate}
            onRepick={reset}
          />
        )}

        {photoPreviewUrl
          && (flow.step === 'confirming' || flow.step === 'enriching'
            || flow.step === 'reviewing' || flow.step === 'committing') && (
          <div className="mt-3">
            <PhotoThumbnail src={photoPreviewUrl} />
          </div>
        )}

        {(flow.step === 'confirming' || flow.step === 'enriching') && flow.step === 'confirming' && (
          <FieldConfirmList
            candidate={flow.candidate}
            edits={editedFields}
            onEdit={setField}
            onContinue={() => void confirmAndEnrich()}
          />
        )}

        {(flow.step === 'reviewing' || flow.step === 'committing') && flow.step === 'reviewing' && (
          <>
            <EnrichedReview
              fields={flow.fields}
              logo={flow.logo}
              stagesFailed={flow.stagesFailed}
              edits={editedFields}
              onEdit={setField}
            />
            <CategoryPills
              guesses={flow.categoryGuesses}
              selected={selectedCategories}
              allCategories={allCategories}
              onToggle={toggleCategory}
            />
            <button
              type="button"
              disabled={selectedCategories.length === 0}
              onClick={() => void save()}
              className="mt-4 w-full rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-50"
            >
              Save to STASH
            </button>
          </>
        )}

        {flow.step === 'resolving' && (
          <MatchResolutionSheet match={flow.match} onResolve={(resolution) => void resolveMatch(resolution)} />
        )}

        {flow.step === 'done' && (
          <DoneStep stashCardId={flow.stashCardId} createdVendor={flow.createdVendor} />
        )}

        {flow.step === 'error' && flow.error.kind === 'quotaExceeded' && (
          <QuotaState
            {...(flow.error.resetsAt ? { resetsAt: flow.error.resetsAt } : {})}
            onDismiss={() => { reset(); navigate('/app/stashboard'); }}
          />
        )}

        {flow.step === 'error' && flow.error.kind !== 'quotaExceeded' && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="m-0 text-sm text-muted-foreground">
              {ERROR_COPY[flow.error.kind] ?? 'Something went wrong.'}
            </p>
            <div className="flex gap-2">
              {(flow.error.kind === 'payloadTooLarge' || flow.error.kind === 'unsupportedImage') ? (
                <button type="button" onClick={reset}
                  className="rounded-full bg-primary px-5 py-2.5 font-semibold text-primary-foreground">
                  Try another photo
                </button>
              ) : (
                <button type="button" onClick={() => void retry()}
                  className="rounded-full bg-primary px-5 py-2.5 font-semibold text-primary-foreground">
                  Retry
                </button>
              )}
              <button type="button" onClick={() => { reset(); navigate('/app/stashboard'); }}
                className="rounded-full bg-secondary px-5 py-2.5 font-semibold text-secondary-foreground">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
